import { useStores } from "api/stores";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  DynamicSearch,
  DynamicSearchType,
} from "components/FiltersV2/DynamicSearch";
import { FiltersV2 } from "components/FiltersV2/Filters";
import {
  getNewFiltersOnly,
  parseFiltersForBE,
} from "components/FiltersV2/utils";
import { Subheader } from "components/Header/Subheader/Subheader";
import { OLTable, Row, createHeadItems } from "components/Table/OLTable";
import { calculateCount } from "components/Table/tableHelpers";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { setCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import { clearTablePage, selectTables } from "reduxState/tables/tablesSlice";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { STORE_LIST_ID } from "state/tables/types";
import {
  SelectedFilter,
  SelectedFilter as SelectedFilterV2,
} from "types/filters";
import { ListingActions } from "types/listing";
import { StoreData } from "types/stores";
import { links } from "utilities/links";
import { ManageTimezoneModal } from "./ManageTimezoneModal";

function createItems(items: StoreData[]): Row[] {
  return items.map((item) => ({
    cells: [
      { content: item.code, property: "code" },
      { content: item.currency, property: "currency" },
      { content: item.name, property: "name" },
    ],
    id: item.storeId,
    metadata: { disabled: !item.active, active: item.active },
  }));
}

export function StoreList() {
  const { message } = useMessage();
  const navigate = useNavigate();
  const appDispatch = useAppDispatch();
  const { listing, updateListing } = useListing(STORE_LIST_ID);
  const {
    setPersistentFilters,
    setPersistentListingSorting,
    setColumnOrdering,
  } = useTablesStateHelpers();
  const tables = useAppSelector(selectTables);

  const [isManageTimezoneModalOpen, setManageTimezoneModalOpen] =
    useState(false);
  const [selectedStoreCode, setSelectedStoreCode] = useState<
    string | undefined
  >("");
  const [dynamicSearch, setDynamicSearch] = useState<DynamicSearchType>();
  const [filters, setFilters] = useState<SelectedFilter[]>(
    tables[STORE_LIST_ID]?.filters?.filter(
      (item: SelectedFilter) => item.type !== "dynamic-search",
    ) || [],
  );

  const { useStoreList } = useStores();
  const { data, error } = useStoreList({
    filters: parseFiltersForBE(getNewFiltersOnly(filters)),
    order: listing.order || "asc",
    page: listing.page,
    rows: listing.rowsPerPage,
    sortKey: listing.property || "code",
    dynamicSearch,
  });

  const setSelectedFilters = (filters: SelectedFilterV2[]) => {
    listing.page = 1;
    setPersistentListingSorting(
      listing.order,
      listing.property,
      listing.rowsPerPage,
      1,
      STORE_LIST_ID,
    );

    setFilters(filters);
  };

  const headItems = createHeadItems([
    { headerText: message({ id: "code" }), property: "code", isSortable: true },
    {
      headerText: message({ id: "currency" }),
      property: "currency",
      isSortable: true,
    },
    { headerText: message({ id: "name" }), property: "name", isSortable: true },
  ]);

  const items = data?.items ? createItems(data.items) : [];

  const handleManageTimezone = (
    id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
  ) => {
    setManageTimezoneModalOpen(true);

    const storeCode = data?.items?.find((store) => store?.storeId === id)?.code;
    setSelectedStoreCode(storeCode);
  };

  useEffect(() => {
    const isError = typeof error !== "undefined";
    // Save filters only when data is correct and error not occurs
    if (!isError && data) {
      setPersistentFilters(filters, STORE_LIST_ID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, error, data]);

  return (
    <>
      <Subheader>
        <FiltersV2
          data-test-id="tenant-list"
          donutLabel={message({ id: "tenants" })}
          properties={[
            {
              query: "active",
              label: message({ id: "active" }),
              type: "boolean-status",
            },
            {
              query: "code",
              label: message({ id: "code" }),
              type: "text",
            },
            {
              query: "currency",
              label: message({ id: "currency" }),
              type: "text",
            },
            {
              query: "storeId",
              label: message({ id: "tenantId" }),
              type: "text",
            },
            {
              query: "name",
              label: message({ id: "name" }),
              type: "tenant-name",
            },
          ]}
          setFilters={setSelectedFilters}
          savedFilters={getNewFiltersOnly(filters)}
          total={data?.total}
        />
        <DynamicSearch
          property="name"
          properties={[
            { id: "code", label: "code" },
            { id: "currency", label: "currency" },
            { id: "name", label: "name" },
          ]}
          setDynamicSearch={setDynamicSearch}
          onSearch={() =>
            appDispatch(clearTablePage({ tableName: STORE_LIST_ID }))
          }
        />
      </Subheader>
      <OLTable
        fullView
        setColumnOrdering={setColumnOrdering}
        tableName={STORE_LIST_ID}
        data-test-id="tenant-list"
        onRowClick={(id: string) => {
          navigate(links.editStore(id as string));
        }}
        sort={{
          order: listing.order || "asc",
          property: listing.property || "code",
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
        }}
        actions={{
          body: [
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => navigate(links.editStore(id as string)),
              label: message({ id: "editTenant" }),
            },
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => handleManageTimezone(id),
              label: message({ id: "manageTimezone" }),
            },
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                const tenantToSwitch = data?.items.find(
                  (item) => item.storeId === id,
                );
                tenantToSwitch &&
                  appDispatch(
                    setCurrentStoreData({
                      code: tenantToSwitch.code,
                      currency: tenantToSwitch.currency,
                      name: tenantToSwitch.name,
                      id: tenantToSwitch.storeId,
                    }),
                  );
              },
              label: message({ id: "switchToInactiveTenant" }),
              hideCondition: (item: Row) => item.metadata.active,
            },
          ],
        }}
        columns={data && headItems}
        rows={data && items}
        onSort={(prop) =>
          updateListing({
            payload: prop,
            type: ListingActions.SET_SORT,
          })
        }
        pagination={{
          count: calculateCount(data?.total.filtered, data?.total.all),
          onChangePage: (page) =>
            updateListing({ payload: page, type: ListingActions.CHANGE_PAGE }),
          onChangeRowsPerPage: (rows) =>
            updateListing({
              payload: rows,
              type: ListingActions.CHANGE_ROWS_PER_PAGE,
            }),
          page: listing.page,
          rowsPerPage: listing.rowsPerPage,
        }}
      />
      {selectedStoreCode && (
        <ManageTimezoneModal
          selectedStoreCode={selectedStoreCode}
          isModalOpen={isManageTimezoneModalOpen}
          closeModal={() => setManageTimezoneModalOpen(false)}
        />
      )}
    </>
  );
}
